import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiBookmark } from "react-icons/fi";
import {
  type MarketDialogueQuestionDto,
  type ReceivedMarketDialogueAnswerDto,
  useMarkMarketDialogueAnswerMutation,
} from "../../autogen/bff-api";
import { StatusDotLabel } from "../../common/components/StatusDotLabel";
import { useApiError } from "../../common/errors/useApiError";
import { GivenAnswer } from "./GivenAnswer";

interface Props {
  marketDialogueId: string;
  questions: MarketDialogueQuestionDto[];
  answers: ReceivedMarketDialogueAnswerDto[];
  canMark: boolean;
  onOpen: (answerId: string) => void;
}

export const MarketDialogueAnswersByQuestion = ({
  marketDialogueId,
  questions,
  answers,
  canMark,
  onOpen,
}: Props) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="8" data-testid="answers-by-question">
      {questions.map((question, index) => (
        <Box key={question.id ?? index}>
          <Heading as="h3" size="sm" mb="3" whiteSpace="pre-wrap">
            {index + 1}. {question.text}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="3">
            {answers.map((answer) => {
              const given = answer.answers.find(
                (a) => a.questionId === question.id,
              );
              return (
                <Card
                  key={answer.id}
                  variant="outline"
                  rounded="xl"
                  borderWidth={given?.marked ? "2px" : "1px"}
                  borderColor={given?.marked ? "smPrimarySoft" : "smBorder"}
                  bg={given?.marked ? "smPrimarySofter" : "smBackground"}
                  data-testid="answer-card"
                >
                  <CardBody p="3">
                    <Flex justify="space-between" align="center" gap="2" mb="1">
                      <Text fontWeight="semibold" fontSize="sm">
                        {answer.supplierName}
                      </Text>
                      {canMark && given && question.id ? (
                        <MarkButton
                          marketDialogueId={marketDialogueId}
                          answerId={answer.id}
                          questionId={question.id}
                          marked={given.marked ?? false}
                        />
                      ) : (
                        given?.marked && (
                          <StatusDotLabel
                            variant="ok"
                            label={t("Taken forward")}
                          />
                        )
                      )}
                    </Flex>
                    <GivenAnswer
                      question={question}
                      given={given}
                      maxHeight="48"
                      compact
                    />
                    <Button
                      mt="2"
                      size="xs"
                      variant="link"
                      colorScheme="teal"
                      onClick={() => onOpen(answer.id)}
                    >
                      {t("Read the whole answer")}
                    </Button>
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      ))}
    </Stack>
  );
};

const MarkButton = ({
  marketDialogueId,
  answerId,
  questionId,
  marked,
}: {
  marketDialogueId: string;
  answerId: string;
  questionId: string;
  marked: boolean;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [mark, { isLoading }] = useMarkMarketDialogueAnswerMutation();
  const toggle = async () => {
    try {
      await mark({
        marketDialogueId,
        markMarketDialogueAnswerRequest: {
          answerId,
          questionId,
          marked: !marked,
        },
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };
  return (
    <Button
      size="xs"
      variant={marked ? "solid" : "outline"}
      colorScheme="teal"
      leftIcon={<FiBookmark />}
      aria-pressed={marked}
      onClick={() => void toggle()}
      isLoading={isLoading}
    >
      {marked ? t("Taken forward") : t("Take forward")}
    </Button>
  );
};
